import { clsx } from 'clsx';
import * as Y from 'yjs';
import { Divider, IconButton, Icon, Tooltip, icon } from '@nula/core-ui';
import type { FormattingState } from '@/features/canvas/engine/hooks/use-formatting-state';
import { HoldToUnlockButton } from '../../primitives/actions';
import { FrameFormattingToolbar } from './frame-formatting-toolbar';
import { requestEditTitle } from '@/features/canvas/engine/nodes/frame/edit-title-signal';
import { FRAME_ACTIONS, type FrameActionContext } from '@/features/canvas/engine/selection/wiring/actions/frame-actions';
import { canvasRoots } from '@/features/canvas/engine/wiring/canvas-roots';
import { useCanvasBinding } from '@/features/canvas/engine/wiring/contexts/canvas-binding-context';
import styles from './frame-floating-toolbar.module.css';

export interface FrameFloatingToolbarProps {
  doc: Y.Doc;
  nodeId: string;
  locked?: boolean;
  collapsed: boolean;
  /** Current frame.data.icon — needed by the icon picker trigger. Fill +
   *  stroke flow through formattingState (state.fillColor / strokeColor)
   *  the same way they do for Sticky and Shape. */
  icon: string;
  formattingState: FormattingState;
  onDelete: (nodeId: string) => void;
  onToggleLock?: (nodeId: string) => void;
  /** Detach all children + delete the frame. Wired by the parent shell
   *  through useFrameUngroup so this component stays presentation-only.
   *  Required (not optional) so the frame surface can never silently
   *  drop the action — if the host doesn't pass a handler, render fails
   *  loudly during prop typing. */
  onUngroup: (nodeId: string) => void;
  className?: string;
}

/**
 * Floating context toolbar for frame nodes. The 1-click action set
 * (edit title, toggle collapse, ungroup, lock, delete) is rendered
 * from FRAME_ACTIONS — the same registry the right-click menu's frame
 * branch consumes. Adding an action there lights it up here too.
 *
 * Formatting (Fill/Border + icon picker) stays in
 * FrameFormattingToolbar because its UX is popover-based and doesn't
 * fit the registry's "single click → run" shape.
 */
export function FrameFloatingToolbar({
  doc,
  nodeId,
  locked = false,
  collapsed,
  icon: iconName,
  formattingState,
  onDelete,
  onToggleLock,
  onUngroup,
  className,
}: FrameFloatingToolbarProps) {
  const binding = useCanvasBinding();
  if (locked && onToggleLock) {
    return (
      <div className={clsx(styles.root, className)} role="toolbar" aria-label="Frame actions">
        <HoldToUnlockButton onUnlock={() => onToggleLock(nodeId)} />
      </div>
    );
  }

  // Toggle collapsed lives close to the toolbar because it needs the
  // doc to write data.collapsed. Wrapped here once and threaded into
  // ctx so the registry stays Yjs-free.
  const toggleCollapsed = () => {
    const nodes = canvasRoots(doc, binding).nodes;
    const node = nodes.get(nodeId);
    if (!node) return;
    const data = node.get('data');
    if (!(data instanceof Y.Map)) return;
    doc.transact(() => data.set('collapsed', !collapsed), doc.clientID);
  };

  // Build the per-render context the registry's available()/run() take.
  // onToggleLock is optional on this component but required by the
  // registry context — surfaces that don't expose lock pass a no-op.
  const ctx: FrameActionContext = {
    nodeId,
    locked,
    collapsed,
    onEditTitle: (id) => requestEditTitle(id),
    onToggleCollapsed: () => toggleCollapsed(),
    onUngroup,
    onToggleLock: onToggleLock ?? (() => {}),
    onDelete,
  };

  // edit-title renders before the divider alongside formatting controls.
  // All other actions render after the divider.
  const editTitleAction = FRAME_ACTIONS.find((a) => a.id === 'edit-title');
  const secondaryActions = FRAME_ACTIONS.filter(
    (a) => a.id !== 'edit-title' && a.available(ctx),
  );

  return (
    <div className={clsx(styles.root, className)} role="toolbar" aria-label="Frame actions">
      <FrameFormattingToolbar
        state={formattingState}
        doc={doc}
        nodeId={nodeId}
        icon={iconName}
      />

      {editTitleAction && editTitleAction.available(ctx) && (
        <Tooltip title={editTitleAction.label} placement="top">
          <IconButton
            transparent
            aria-label={editTitleAction.label}
            className={styles.btn}
            onClick={() => editTitleAction.run(ctx)}
          >
            <Icon name={editTitleAction.icon} size={icon.md} />
          </IconButton>
        </Tooltip>
      )}

      <Divider orientation="vertical" className={styles.divider} />

      {/* Secondary actions: toggle-collapse, lock, ungroup, delete */}
      {secondaryActions.map((action) => {
        const label = action.labelFor ? action.labelFor(ctx) : action.label;
        const actionIcon = action.iconFor ? action.iconFor(ctx) : action.icon;
        return (
          <Tooltip key={action.id} title={label} placement="top">
            <IconButton
              transparent
              aria-label={label}
              aria-pressed={action.id === 'toggle-collapse' ? collapsed : undefined}
              className={styles.btn}
              onClick={() => action.run(ctx)}
            >
              <Icon name={actionIcon} size={icon.md} />
            </IconButton>
          </Tooltip>
        );
      })}
    </div>
  );
}
